import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Download, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Field, NativeSelect, PageHeader } from "@/components/common/Page";
import { ErrorState, LoadingState, Spinner } from "@/components/common/States";
import { PhotoUpload } from "@/components/common/PhotoUpload";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { PlanCards } from "@/components/billing/PlanCards";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, API } from "@/lib/api";
import { fmtDate, METHODS, WEEKDAYS_SHORT } from "@/lib/format";

const Card = ({ title, children }) => (
  <div className="rounded-xl border bg-card p-5 sm:p-6"><h3 className="mb-5 font-heading text-lg font-semibold">{title}</h3>{children}</div>
);

const useSave = (onDone) => {
  const [busy, setBusy] = useState(false);
  const save = async (payload, msg = "Configurações salvas") => {
    setBusy(true);
    try {
      await api.put("/profile", payload);
      toast.success(msg);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, save };
};

const ProfileTab = ({ profile, reload }) => {
  const { refresh } = useAuth();
  const [f, setF] = useState(profile);
  const { busy, save } = useSave(() => { reload(); refresh(); });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Card title="Perfil">
      <form onSubmit={(e) => { e.preventDefault(); save({ first_name: f.first_name, last_name: f.last_name, phone: f.phone, city: f.city, state: f.state, professional_name: f.professional_name, area: f.area, photo_path: f.photo_path }); }} className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><PhotoUpload value={f.photo_path} name={`${f.first_name} ${f.last_name}`} onChange={(p) => setF({ ...f, photo_path: p })} testId="profile-photo" /></div>
        <Field label="Nome"><Input value={f.first_name || ""} onChange={set("first_name")} data-testid="profile-first-name-input" /></Field>
        <Field label="Sobrenome"><Input value={f.last_name || ""} onChange={set("last_name")} data-testid="profile-last-name-input" /></Field>
        <Field label="Nome profissional"><Input value={f.professional_name || ""} onChange={set("professional_name")} data-testid="profile-professional-input" /></Field>
        <Field label="Área de ensino"><Input value={f.area || ""} onChange={set("area")} data-testid="profile-area-input" /></Field>
        <Field label="E-mail" hint={f.email_verified ? "E-mail verificado" : "E-mail ainda não verificado"}><Input value={f.email} disabled data-testid="profile-email-input" /></Field>
        <Field label="Telefone"><Input value={f.phone || ""} onChange={set("phone")} data-testid="profile-phone-input" /></Field>
        <Field label="Cidade"><Input value={f.city || ""} onChange={set("city")} data-testid="profile-city-input" /></Field>
        <Field label="Estado"><Input value={f.state || ""} onChange={set("state")} data-testid="profile-state-input" /></Field>
        <div className="sm:col-span-2"><Button type="submit" disabled={busy} data-testid="profile-save-btn">{busy && <Spinner className="mr-2" />}Salvar perfil</Button></div>
      </form>
    </Card>
  );
};

const ClassesTab = ({ profile, reload }) => {
  const [f, setF] = useState({ default_duration: profile.default_duration, default_price: profile.default_price, modality: profile.modality || "ambas", working_hours: profile.working_hours || { start: "08:00", end: "20:00", days: [1, 2, 3, 4, 5] } });
  const subjects = useFetch("/subjects");
  const [newSubject, setNewSubject] = useState("");
  const { busy, save } = useSave(reload);
  const wh = f.working_hours;
  const toggleDay = (d) => setF({ ...f, working_hours: { ...wh, days: wh.days.includes(d) ? wh.days.filter((x) => x !== d) : [...wh.days, d].sort() } });
  const addSubject = async () => {
    if (!newSubject.trim()) return;
    try { await api.post("/subjects", { name: newSubject.trim() }); setNewSubject(""); subjects.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const removeSubject = async (id) => { await api.delete(`/subjects/${id}`); subjects.reload(); };
  return (
    <div className="space-y-6">
      <Card title="Aulas">
        <form onSubmit={(e) => { e.preventDefault(); save({ ...f, default_duration: Number(f.default_duration), default_price: Number(f.default_price) }); }} className="grid gap-4 sm:grid-cols-3">
          <Field label="Duração padrão (min)"><Input type="number" min="15" max="480" value={f.default_duration} onChange={(e) => setF({ ...f, default_duration: e.target.value })} data-testid="settings-duration-input" /></Field>
          <Field label="Valor padrão (R$)"><Input type="number" min="0" step="0.01" value={f.default_price} onChange={(e) => setF({ ...f, default_price: e.target.value })} data-testid="settings-price-input" /></Field>
          <Field label="Modalidade">
            <NativeSelect value={f.modality} onChange={(e) => setF({ ...f, modality: e.target.value })} data-testid="settings-modality-select">
              <option value="presencial">Presencial</option><option value="online">Online</option><option value="ambas">Ambas</option>
            </NativeSelect>
          </Field>
          <Field label="Início do expediente"><Input type="time" value={wh.start} onChange={(e) => setF({ ...f, working_hours: { ...wh, start: e.target.value } })} data-testid="settings-hours-start-input" /></Field>
          <Field label="Fim do expediente"><Input type="time" value={wh.end} onChange={(e) => setF({ ...f, working_hours: { ...wh, end: e.target.value } })} data-testid="settings-hours-end-input" /></Field>
          <div className="sm:col-span-3">
            <p className="mb-2 text-sm font-medium">Dias de atendimento</p>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS_SHORT.map((label, i) => {
                const d = (i + 1) % 7;
                return <button type="button" key={label} onClick={() => toggleDay(d)} className={`rounded-lg border px-3 py-1.5 text-sm ${wh.days.includes(d) ? "border-primary bg-primary/10 text-primary" : ""}`} data-testid={`settings-day-${d}`}>{label}</button>;
              })}
            </div>
          </div>
          <div className="sm:col-span-3"><Button type="submit" disabled={busy} data-testid="settings-classes-save-btn">Salvar</Button></div>
        </form>
      </Card>
      <Card title="Disciplinas">
        <div className="mb-4 flex gap-2"><Input placeholder="Nova disciplina" value={newSubject} onChange={(e) => setNewSubject(e.target.value)} data-testid="new-subject-input" /><Button type="button" onClick={addSubject} data-testid="add-subject-btn">Adicionar</Button></div>
        <div className="flex flex-wrap gap-2">
          {(subjects.data || []).map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-sm" data-testid={`subject-chip-${s.id}`}>
              {s.name}<button onClick={() => removeSubject(s.id)} aria-label={`Remover ${s.name}`} data-testid={`remove-subject-${s.id}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      </Card>
    </div>
  );
};

const FinanceTab = ({ profile, reload }) => {
  const { hasFeature } = useAuth();
  const [due, setDue] = useState(profile.default_due_day || 10);
  const [methods, setMethods] = useState(profile.payment_methods || []);
  const [reminders, setReminders] = useState(profile.email_reminders !== false);
  const [guardians, setGuardians] = useState(profile.guardian_reminders !== false);
  const [sending, setSending] = useState(false);
  const { busy, save } = useSave(reload);
  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/notifications/email-digest");
      data.sent ? toast.success(data.message) : toast.info(data.message);
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };
  return (
    <div className="space-y-6">
    <Card title="Lembretes por e-mail">
      {hasFeature("lembretes") ? (
        <div className="space-y-4">
          <label className="flex items-start gap-3 text-sm">
            <Checkbox className="mt-0.5" checked={reminders} onCheckedChange={(c) => { setReminders(!!c); save({ email_reminders: !!c }, c ? "Resumo diário ativado" : "Resumo diário desativado"); }} data-testid="settings-email-reminders-checkbox" />
            <span>Receber todo dia, às 7h, um e-mail com as aulas de amanhã e as mensalidades vencendo ou atrasadas.</span>
          </label>
          <label className="flex items-start gap-3 text-sm">
            <Checkbox className="mt-0.5" checked={guardians} onCheckedChange={(c) => { setGuardians(!!c); save({ guardian_reminders: !!c }, c ? "Avisos aos responsáveis ativados" : "Avisos aos responsáveis desativados"); }} data-testid="settings-guardian-reminders-checkbox" />
            <span>Avisar os responsáveis por e-mail um dia antes de cada aula e quando uma mensalidade atrasar (usa o e-mail do responsável no cadastro do aluno e os acessos de responsável do portal).</span>
          </label>
          <Button variant="outline" disabled={sending} onClick={sendNow} data-testid="send-digest-now-btn">{sending && <Spinner className="mr-2" />}Enviar resumo agora</Button>
        </div>
      ) : <p className="text-sm text-muted-foreground" data-testid="email-reminders-locked">O resumo diário por e-mail está disponível no plano Profissional.</p>}
    </Card>
    <Card title="Financeiro">
      <div className="space-y-5">
        <Field label="Dia padrão de vencimento" className="max-w-xs"><Input type="number" min="1" max="28" value={due} onChange={(e) => setDue(e.target.value)} data-testid="settings-due-day-input" /></Field>
        <div>
          <p className="mb-2 text-sm font-medium">Formas de pagamento aceitas</p>
          <div className="flex flex-wrap gap-4">
            {Object.entries(METHODS).map(([k, v]) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <Checkbox checked={methods.includes(k)} onCheckedChange={(c) => setMethods(c ? [...methods, k] : methods.filter((m) => m !== k))} data-testid={`settings-method-${k}`} />{v}
              </label>
            ))}
          </div>
        </div>
        <Button disabled={busy} onClick={() => save({ default_due_day: Number(due), payment_methods: methods })} data-testid="settings-finance-save-btn">Salvar</Button>
      </div>
    </Card>
    </div>
  );
};

const AccountTab = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [delPw, setDelPw] = useState("");
  const [confirm, setConfirm] = useState(false);
  const change = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/change-password", pw);
      toast.success(data.message);
      setPw({ current_password: "", new_password: "" });
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async () => {
    try {
      await api.delete("/account", { data: { password: delPw } });
      toast.success("Conta excluída");
      await logout();
      navigate("/login");
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="space-y-6">
      <Card title="Alterar senha">
        <form onSubmit={change} className="grid gap-4 sm:grid-cols-2">
          <Field label="Senha atual"><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required data-testid="current-password-input" /></Field>
          <Field label="Nova senha" hint="Mínimo 8 caracteres, com letras e números"><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required data-testid="new-password-input" /></Field>
          <div><Button type="submit" data-testid="change-password-btn">Alterar senha</Button></div>
        </form>
      </Card>
      <Card title="Exportação de dados (LGPD)">
        <p className="mb-4 text-sm text-muted-foreground">Baixe uma cópia de todos os seus dados e dos dados dos seus alunos em formato JSON.</p>
        <Button asChild variant="outline" data-testid="export-data-btn"><a href={`${API}/account/export`}><Download className="mr-2 h-4 w-4" />Exportar meus dados</a></Button>
      </Card>
      <Card title="Excluir conta">
        <p className="mb-4 text-sm text-muted-foreground">Remove permanentemente sua conta, alunos, aulas e pagamentos. Esta ação não pode ser desfeita.</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input type="password" placeholder="Confirme sua senha" value={delPw} onChange={(e) => setDelPw(e.target.value)} className="sm:max-w-xs" data-testid="delete-account-password-input" />
          <Button variant="destructive" disabled={!delPw} onClick={() => setConfirm(true)} data-testid="delete-account-btn">Excluir minha conta</Button>
        </div>
      </Card>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title="Excluir sua conta definitivamente?" destructive confirmLabel="Excluir tudo"
        description="Todos os dados serão apagados permanentemente." onConfirm={remove} />
    </div>
  );
};

const SubscriptionTab = () => {
  const { refresh } = useAuth();
  const { data, reload } = useFetch("/subscription");
  const [confirm, setConfirm] = useState(false);
  const [opening, setOpening] = useState(false);
  useEffect(() => { if (data) refresh(); }, [data, refresh]);
  if (!data) return <LoadingState rows={2} />;
  const openPortal = async () => {
    setOpening(true);
    try {
      const { data: res } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = res.url;
    } catch (e) { toast.error(errMsg(e)); setOpening(false); }
  };
  const cancel = async () => {
    try { await api.post("/billing/cancel"); toast.success("Assinatura cancelada. Você está no plano Gratuito."); reload(); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6">
      <Card title="Sua assinatura">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-heading text-2xl font-bold" data-testid="current-plan-name">Plano {data.plan.name}</p>
            <p className="text-sm text-muted-foreground">{data.usage.alunos_ativos} aluno(s) ativo(s){data.plan.max_students ? ` de ${data.plan.max_students}` : " · ilimitado"}{data.current_period_end && ` · renova em ${fmtDate(data.current_period_end.slice(0, 10))}`}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.has_billing_portal && <Button variant="outline" disabled={opening} onClick={openPortal} data-testid="billing-portal-btn">{opening && <Spinner className="mr-2" />}Gerenciar cartão e faturas</Button>}
            {data.plan_id !== "free" && <Button variant="outline" onClick={() => setConfirm(true)} data-testid="cancel-subscription-btn">Cancelar assinatura</Button>}
          </div>
        </div>
        {data.status === "past_due" && (
          <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm" data-testid="subscription-past-due-alert">
            O pagamento da renovação falhou. Atualize o cartão até <strong>{fmtDate(data.grace_until?.slice(0, 10))}</strong> para não voltar ao plano Gratuito.
          </div>
        )}
      </Card>
      <PlanCards currentPlanId={data.plan_id} />
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title="Cancelar assinatura?" destructive confirmLabel="Cancelar assinatura"
        description="Sua conta voltará ao plano Gratuito (até 5 alunos ativos, sem financeiro e relatórios)." onConfirm={cancel} />
    </div>
  );
};

export default function Settings() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "perfil";
  const { data, error, reload } = useFetch("/profile");
  const [key, setKey] = useState(0);
  useEffect(() => setKey((k) => k + 1), [data]);
  return (
    <div className="animate-in fade-in duration-300">
      <PageHeader title="Configurações" subtitle="Perfil, preferências de aulas, financeiro e conta" />
      {error ? <ErrorState error={error} onRetry={reload} /> : !data ? <LoadingState /> : (
        <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
          <TabsList className="mb-4 h-auto w-full justify-start overflow-x-auto">
            {[["perfil", "Perfil"], ["aulas", "Aulas"], ["financeiro", "Financeiro"], ["conta", "Conta"], ["assinatura", "Assinatura"]].map(([k, l]) => (
              <TabsTrigger key={k} value={k} data-testid={`settings-tab-${k}`}>{l}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="perfil"><ProfileTab key={key} profile={data} reload={reload} /></TabsContent>
          <TabsContent value="aulas"><ClassesTab key={key} profile={data} reload={reload} /></TabsContent>
          <TabsContent value="financeiro"><FinanceTab key={key} profile={data} reload={reload} /></TabsContent>
          <TabsContent value="conta"><AccountTab /></TabsContent>
          <TabsContent value="assinatura"><SubscriptionTab /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
